import { uniqBy } from 'lodash-es';

import type { VStorageProgress } from '@/repository';
import type { PCDData, PcdFileCacheStatus } from '@/type';
import appWorker from '@/worker';

import { usePcdCacheStore } from '../pcdCacheStore';
import useTenantStorageStore from '../tenantStorageStore';

const getStorageState = () => useTenantStorageStore.getState();

export const getPcdInCache = (pcdPath: string) =>
  usePcdCacheStore.getState().getPcd(pcdPath);

export const getPcdPath = (path: string, fileName: string) => ({
  path,
  fileName,
  toString: () => `${path}/${fileName}`,
});

export const getPcdPathMap = (dirPath: string) =>
  usePcdCacheStore.getState().getPcdPathMap(dirPath);

export const getPcdFileStatus = (pcdPath: string) =>
  usePcdCacheStore.getState().getPcdStatus(pcdPath);

export const getPcdFileStatusMapByLocal = (dirPath: string) =>
  usePcdCacheStore.getState().getPcdStatusMap(dirPath);

export const getPcdNameList = (
  dirPath: string,
  progressCallback?: (progress: VStorageProgress) => void,
) => {
  return usePcdCacheStore
    .getState()
    .getPcdPathMap(dirPath)
    .then(pcdFileMap =>
      Object.entries(pcdFileMap).reduce((acc, cur) => {
        acc[+cur[0]] = cur[1];

        return acc;
      }, [] as string[]),
    )
    .then(pcdFileListInLocal => {
      if (pcdFileListInLocal?.length) {
        progressCallback?.({
          loaded: pcdFileListInLocal.length,
          total: pcdFileListInLocal.length,
          rate: 100,
        });

        return Promise.resolve(
          pcdFileListInLocal.filter(file => file.endsWith('.pcd')),
        );
      }

      return readDirectoryRecursive(dirPath, progressCallback).then(
        ({ fileList }) =>
          uniqBy(fileList, v => v.key)
            .filter(file => file.key && file.key.endsWith('.pcd'))
            .map(file => file.key!.replace(new RegExp(`(/?)${dirPath}/`), '')),
      );
    });
};

export const readDirectoryRecursive = (
  path: string,
  progressCallback?: (progress: VStorageProgress) => void,
) => {
  const param = {
    prefix: path,
    progressCallback,
  };
  return getStorageState().readDirectoryRecursive(param);
};

export const readFile = (key: string) => {
  return getStorageState()
    .readFile({ key, timestamp: true })
    .then(async ({ key, body }) => {
      const value = body ? new Uint8Array(body) : null;

      return { key, value };
    });
};

export const readFileByWorker = (key: string) => {
  const { storageCurrentBucket } = getStorageState();

  return appWorker.request('pcd', 'fetch', {
    bucket: storageCurrentBucket!,
    path: key,
  });
};

export const readFileList = (keyList: string[]) => {
  return getStorageState()
    .readFileList({ keyList, timestamp: true })
    .then(async list => {
      return await Promise.all(
        list.map(async ({ key, body }) => {
          const value = body ? new Uint8Array(body) : null;

          return {
            key,
            value,
          };
        }),
      );
    });
};

export const removeAllPcdInCache = (dirPath: string) => {
  return Promise.allSettled([
    usePcdCacheStore.getState().clearPcdList(dirPath),
    usePcdCacheStore.getState().clearPcdPathMap(dirPath),
    usePcdCacheStore.getState().clearPcdStatusMap(dirPath),
  ]);
};

export const removePcdInCache = (pcdPath: string) =>
  usePcdCacheStore.getState().removePcd(pcdPath);

export const setPcdInCache = (pcdPath: string, data: PCDData) =>
  usePcdCacheStore.getState().setPcd(pcdPath, data);

export const setPcdFileStatus = (
  dirPath: string,
  pcdPath: string,
  status: SelectivePartial<PcdFileCacheStatus, 'optimized'>,
) => {
  return usePcdCacheStore
    .getState()
    .setPcdStatus(dirPath, pcdPath, { optimized: false, ...status });
};

export const setPcdFileStatusMap = (
  dirPath: string,
  entries: [string, PcdFileCacheStatus][],
) => {
  return usePcdCacheStore.getState().setPcdStatusMap(dirPath, entries);
};

export const splitByCachedPcdFileList = async (
  dirPath: string,
  pcdFileList: string[],
  pcdFileFetchable: boolean,
  indexOptions: {
    start?: number;
    end?: number;
  } = {},
) => {
  const fileStatusMap = await getPcdFileStatusMapByLocal(dirPath);
  const splitedFileList = [] as (string[] | string)[];
  const { start = 0, end = pcdFileList.length - 1 } = indexOptions;
  let isNewChunk = true;

  for (let i = start; i <= end; i++) {
    if (!pcdFileFetchable) break;

    const fileName = pcdFileList[i];
    const fileStatus = fileStatusMap[fileName];

    if (fileStatus?.loaded) {
      splitedFileList.push(fileName);

      isNewChunk = true;
    } else if (!fileStatus?.isLoading) {
      const lastSplitedFileList = isNewChunk
        ? []
        : (splitedFileList[splitedFileList.length - 1] as string[]);

      lastSplitedFileList.push(fileName);

      isNewChunk &&
        splitedFileList.push(lastSplitedFileList) &&
        (isNewChunk = false);
    }
  }

  return splitedFileList;
};
